import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { getEmpresaAtual } from './api';
import {
  coresHex, hexParaRgb, escurecer, clarear,
} from '../styles/styleUtils';

// Converte um hex de coresHex em rgb() 0-1 do pdf-lib (que não entende var()/hex).
const rgbHex = (hex) => {
  const { r, g, b } = hexParaRgb(hex);
  return rgb(r / 255, g / 255, b / 255);
};

// ============================================================
// Relatório de Despesas (Obras / Operacionais) — Marcki v2
// Orientação PAISAGEM. Agrupado por unidade, com status.
// ============================================================

const generatePdfRelatorioDespesas = async (data, filtro, modo = 'obras') => {
  if (!data) return;

  const ehObras = modo === 'obras';
  const colNome = ehObras ? 'Obra' : 'Centro de Custo';
  const campoNome = ehObras ? 'obra' : 'centroDeCusto';

  const pdfDoc   = await PDFDocument.create();
  const fontReg  = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  // ── PAISAGEM (A4 deitado) ───────────────────────────────────────────────
  const W = 841.89; const H = 595.28;
  const ML = 40; const MR = 40;
  const CW = W - ML - MR;

  const C = {
    cinzaChumbo: rgbHex(coresHex.chumbo),
    cinzaMedio:  rgbHex(coresHex.cinzaMedio),
    cinzaClaro:  rgbHex(coresHex.cinzaClaro),
    laranja:     rgbHex(coresHex.laranja),
    laranjaClaro:rgbHex(coresHex.laranjaClaro),
    laranjaEsc:  rgbHex(coresHex.laranjaEscuro),
    branco:      rgb(1, 1, 1),
    preto:       rgb(0.133, 0.133, 0.133),
    borda:       rgbHex(coresHex.borda),
    verde:       rgb(0.118, 0.490, 0.204),
    verdeBg:     rgb(0.902, 0.957, 0.918),
    vermelho:    rgb(0.776, 0.157, 0.157),
    vermelhoBg:  rgb(0.992, 0.925, 0.918),
    cinzaBg:     rgb(0.933, 0.933, 0.933),
  };

  const brl = (v) => parseFloat(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const drawRect  = (pg, x, y, w, h, color) => pg.drawRectangle({ x, y, width: w, height: h, color });
  const drawBRect = (pg, x, y, w, h, fill, border) => pg.drawRectangle({ x, y, width: w, height: h, color: fill, borderColor: border || C.borda, borderWidth: 0.5 });
  const drawText  = (pg, t, x, y, s, f, color) => pg.drawText(String(t ?? ''), { x, y, size: s, font: f, color });

  const tH = 15;
  const drawRow = (pg, cells, y) => {
    cells.forEach((c) => {
      drawBRect(pg, c.x, y - tH + 2, c.w, tH, c.fill, c.bc || C.borda);
      if (c.text != null && c.text !== '') {
        const sz = c.sz || 8; const fn = c.fn || fontReg;
        let tx = c.x + 4;
        if (c.al === 'r') tx = c.x + c.w - fn.widthOfTextAtSize(String(c.text), sz) - 4;
        if (c.al === 'c') tx = c.x + (c.w - fn.widthOfTextAtSize(String(c.text), sz)) / 2;
        drawText(pg, c.text, tx, y - tH + 5, sz, fn, c.cl || C.preto);
      }
    });
    return y - tH;
  };

  // Chip de status desenhado (fundo colorido + texto)
  const drawStatus = (pg, status, x, y, w) => {
    let bg = C.cinzaBg; let cor = C.cinzaMedio;
    if (status === 'Pago') { bg = C.verdeBg; cor = C.verde; }
    else if (status === 'Vencido') { bg = C.vermelhoBg; cor = C.vermelho; }
    const txtW = fontBold.widthOfTextAtSize(status, 7.5);
    const chipW = Math.min(txtW + 12, w - 6);
    drawRect(pg, x + 4, y - tH + 3.5, chipW, tH - 5, bg);
    drawText(pg, status, x + 4 + (chipW - txtW) / 2, y - tH + 6, 7.5, fontBold, cor);
  };

  // ── Logo ────────────────────────────────────────────────────────────────
  let logoImage = null;
  let empresa = null;
  try {
    empresa = await getEmpresaAtual();
    if (empresa?.logoBranca) {
      const base64 = empresa.logoBranca.split(',')[1];
      const bin = atob(base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
      logoImage = await pdfDoc.embedPng(bytes);
    }
  } catch (e) { /* sem logo */ }

  const drawHeader = (pg) => {
    drawRect(pg, 0, H - 44, W, 44, C.cinzaChumbo);
    drawRect(pg, 0, H - 44, 6, 44, C.laranja);
    if (logoImage) {
      const lDims = logoImage.scaleToFit(70, 28);
      pg.drawImage(logoImage, { x: ML, y: H - 38, width: lDims.width, height: lDims.height });
    } else {
      drawText(pg, empresa?.nome || 'Marcki Engenharia', ML, H - 26, 11, fontBold, C.branco);
    }
    const titulo = `Relatório de Despesas — ${ehObras ? 'Obras' : 'Operacionais'}`;
    drawText(pg, titulo, W - MR - fontBold.widthOfTextAtSize(titulo, 9), H - 24, 9, fontBold, C.branco);
    const periodo = [
      filtro?.datainicial ? new Date(filtro.datainicial).toLocaleDateString('pt-BR') : '',
      filtro?.datafinal   ? new Date(filtro.datafinal).toLocaleDateString('pt-BR')   : '',
    ].filter(Boolean).join(' — ');
    if (periodo) drawText(pg, periodo, W - MR - fontReg.widthOfTextAtSize(periodo, 8), H - 36, 8, fontReg, rgb(0.8, 0.8, 0.8));
    return H - 58;
  };

  const drawFooter = (pg) => {
    drawRect(pg, 0, 0, W, 26, C.cinzaChumbo);
    drawRect(pg, 0, 0, 6, 26, C.laranja);
    drawText(pg, `${empresa?.nome || 'Marcki Engenharia'} — ${empresa?.enderecoRodape || 'Araguari/MG'}`, ML + 8, 9, 7, fontReg, rgb(0.8, 0.8, 0.8));
    drawText(pg, `Gerado em ${new Date().toLocaleDateString('pt-BR')}`, W - MR - 70, 9, 7, fontReg, rgb(0.8, 0.8, 0.8));
  };

  let page = pdfDoc.addPage([W, H]);
  let y = drawHeader(page);
  const novaPagina = (alturaMin = 60) => {
    if (y < alturaMin) {
      drawFooter(page);
      page = pdfDoc.addPage([W, H]);
      y = drawHeader(page);
      return true;
    }
    return false;
  };

  const dados  = data.dados || [];
  const resumo = data.resumo || [];
  const totais = data.totais || { aPagar: 0, pago: 0, vencido: 0, total: 0 };

  // ── KPIs (5) ──────────────────────────────────────────────────────────────
  y -= 8;
  const kpiGap = 6;
  const kpiW = (CW - kpiGap * 4) / 5;
  const kpiH = 38;
  const kpis = [
    { label: 'Lançamentos', valor: String(dados.length) },
    { label: 'A Pagar',     valor: brl(totais.aPagar) },
    { label: 'Vencido',     valor: brl(totais.vencido) },
    { label: 'Pago',        valor: brl(totais.pago) },
    { label: 'Total Geral', valor: brl(totais.total) },
  ];
  kpis.forEach((kpi, i) => {
    const x = ML + i * (kpiW + kpiGap);
    drawBRect(page, x, y - kpiH, kpiW, kpiH, C.laranjaClaro, C.laranja);
    drawRect(page, x, y - kpiH, 3, kpiH, C.laranja);
    drawText(page, kpi.label, x + 6, y - 14, 7.5, fontReg, C.cinzaMedio);
    drawText(page, kpi.valor, x + 6, y - 30, kpi.valor.length > 14 ? 9 : 11, fontBold, C.cinzaChumbo);
  });
  y -= kpiH + 16;

  // ── Larguras das colunas da tabela de detalhe ─────────────────────────────
  const e1 = CW * 0.09;  // Data
  const e2 = CW * 0.40;  // Descrição
  const e3 = CW * 0.22;  // Obra / Centro
  const e4 = CW * 0.10;  // Status
  const e5 = CW * 0.09;  // Data Ref
  const e6 = CW - e1 - e2 - e3 - e4 - e5; // Valor

  const cabecalhoTabela = () => {
    y = drawRow(page, [
      { x: ML,                     w: e1, fill: C.laranja, text: 'Data',     fn: fontBold, cl: C.branco },
      { x: ML+e1,                  w: e2, fill: C.laranja, text: 'Descrição', fn: fontBold, cl: C.branco },
      { x: ML+e1+e2,               w: e3, fill: C.laranja, text: colNome,    fn: fontBold, cl: C.branco },
      { x: ML+e1+e2+e3,            w: e4, fill: C.laranja, text: 'Status',   fn: fontBold, cl: C.branco, al: 'c' },
      { x: ML+e1+e2+e3+e4,         w: e5, fill: C.laranja, text: 'Data Ref', fn: fontBold, cl: C.branco },
      { x: ML+e1+e2+e3+e4+e5,      w: e6, fill: C.laranja, text: 'Valor',    fn: fontBold, cl: C.branco, al: 'r' },
    ], y);
  };

  // ── Agrupa por unidade ────────────────────────────────────────────────────
  const porUnidade = dados.reduce((acc, l) => {
    const u = l.unidade || '—';
    if (!acc[u]) acc[u] = [];
    acc[u].push(l);
    return acc;
  }, {});
  const unidades = Object.keys(porUnidade).sort();

  if (dados.length === 0) {
    drawText(page, `Nenhuma despesa ${ehObras ? 'de obra' : 'operacional'} no período selecionado.`, ML + 2, y - 16, 9, fontReg, C.cinzaMedio);
    y -= 24;
  }

  unidades.forEach((u) => {
    novaPagina(90);
    // Título da unidade
    drawRect(page, ML, y + 2, CW, 16, C.cinzaChumbo);
    drawText(page, `UNIDADE: ${String(u).toUpperCase()}`, ML + 6, y + 7, 8, fontBold, C.branco);
    y -= 16;
    cabecalhoTabela();

    let totalUnidade = 0;
    porUnidade[u].forEach((l, i) => {
      if (novaPagina(50)) cabecalhoTabela();
      const fill = i % 2 === 0 ? C.cinzaClaro : C.branco;
      const xStatus = ML + e1 + e2 + e3;
      const bandTop = y;
      y = drawRow(page, [
        { x: ML,                w: e1, fill, text: l.data },
        { x: ML+e1,             w: e2, fill, text: (l.descricao || '').substring(0, 60) },
        { x: ML+e1+e2,          w: e3, fill, text: (l[campoNome] || '').substring(0, 34) },
        { x: xStatus,           w: e4, fill, text: '' },
        { x: ML+e1+e2+e3+e4,    w: e5, fill, text: l.dataRef || '—' },
        { x: ML+e1+e2+e3+e4+e5, w: e6, fill, text: brl(l.valor), al: 'r' },
      ], y);
      drawStatus(page, l.status, xStatus, bandTop, e4);
      totalUnidade += (l.valor || 0);
    });

    // Total da unidade
    if (novaPagina(50)) cabecalhoTabela();
    y = drawRow(page, [
      { x: ML,                w: e1 + e2 + e3 + e4 + e5, fill: C.laranjaClaro, bc: C.laranja, text: `Total ${u}`, fn: fontBold, cl: C.laranjaEsc },
      { x: ML+e1+e2+e3+e4+e5, w: e6, fill: C.laranjaClaro, bc: C.laranja, text: brl(totalUnidade), fn: fontBold, cl: C.laranjaEsc, al: 'r' },
    ], y);
    y -= 10;
  });

  // ── Resumo ────────────────────────────────────────────────────────────────
  if (resumo.length > 0) {
    novaPagina(120);
    drawRect(page, ML, y + 2, CW, 16, C.cinzaChumbo);
    drawText(page, `RESUMO POR ${colNome.toUpperCase()}`, ML + 6, y + 7, 8, fontBold, C.branco);
    y -= 16;

    const r1 = CW * 0.36;
    const r2 = CW * 0.14;
    const r3 = CW * 0.14;
    const r4 = CW * 0.14;
    const r5 = CW * 0.14;
    const r6 = CW - r1 - r2 - r3 - r4 - r5;
    const cabResumo = () => {
      y = drawRow(page, [
        { x: ML,                w: r1, fill: C.laranja, text: colNome, fn: fontBold, cl: C.branco },
        { x: ML+r1,             w: r2, fill: C.laranja, text: 'A Pagar', fn: fontBold, cl: C.branco, al: 'r' },
        { x: ML+r1+r2,          w: r3, fill: C.laranja, text: 'Vencido', fn: fontBold, cl: C.branco, al: 'r' },
        { x: ML+r1+r2+r3,       w: r4, fill: C.laranja, text: 'Pago',    fn: fontBold, cl: C.branco, al: 'r' },
        { x: ML+r1+r2+r3+r4,    w: r5, fill: C.laranja, text: 'Total',   fn: fontBold, cl: C.branco, al: 'r' },
        { x: ML+r1+r2+r3+r4+r5, w: r6, fill: C.laranja, text: '% Total', fn: fontBold, cl: C.branco, al: 'r' },
      ], y);
    };
    cabResumo();
    resumo.forEach((r, i) => {
      if (novaPagina(50)) cabResumo();
      const fill = i % 2 === 0 ? C.cinzaClaro : C.branco;
      y = drawRow(page, [
        { x: ML,                w: r1, fill, text: (r.nome || '').substring(0, 50) },
        { x: ML+r1,             w: r2, fill, text: brl(r.aPagar), al: 'r' },
        { x: ML+r1+r2,          w: r3, fill, text: brl(r.vencido), al: 'r', cl: r.vencido > 0 ? C.vermelho : C.preto },
        { x: ML+r1+r2+r3,       w: r4, fill, text: brl(r.pago), al: 'r', cl: C.verde },
        { x: ML+r1+r2+r3+r4,    w: r5, fill, text: brl(r.total), al: 'r' },
        { x: ML+r1+r2+r3+r4+r5, w: r6, fill, text: `${Number(r.percentual || 0).toFixed(2)}%`, al: 'r' },
      ], y);
    });
    if (novaPagina(50)) cabResumo();
    y = drawRow(page, [
      { x: ML,                w: r1, fill: C.laranjaClaro, bc: C.laranja, text: 'Total Geral', fn: fontBold, cl: C.laranjaEsc },
      { x: ML+r1,             w: r2, fill: C.laranjaClaro, bc: C.laranja, text: brl(totais.aPagar), fn: fontBold, cl: C.laranjaEsc, al: 'r' },
      { x: ML+r1+r2,          w: r3, fill: C.laranjaClaro, bc: C.laranja, text: brl(totais.vencido), fn: fontBold, cl: C.laranjaEsc, al: 'r' },
      { x: ML+r1+r2+r3,       w: r4, fill: C.laranjaClaro, bc: C.laranja, text: brl(totais.pago), fn: fontBold, cl: C.laranjaEsc, al: 'r' },
      { x: ML+r1+r2+r3+r4,    w: r5, fill: C.laranjaClaro, bc: C.laranja, text: brl(totais.total), fn: fontBold, cl: C.laranjaEsc, al: 'r' },
      { x: ML+r1+r2+r3+r4+r5, w: r6, fill: C.laranjaClaro, bc: C.laranja, text: '100%', fn: fontBold, cl: C.laranjaEsc, al: 'r' },
    ], y);
  }

  drawFooter(page);

  const pdfBytes = await pdfDoc.save();
  const blob     = new Blob([pdfBytes], { type: 'application/pdf' });
  const url      = URL.createObjectURL(blob);
  const link     = document.createElement('a');
  link.href      = url;
  link.download  = `relatorio-despesas-${ehObras ? 'obras' : 'operacionais'}.pdf`;
  link.click();
  URL.revokeObjectURL(url);
};

export default generatePdfRelatorioDespesas;